import { useState, useEffect } from 'react';
import { useAuth } from '../utils/AuthContext';
import { getDataOnRealtimeDb, saveDataOnRealtimeDb, updateDataOnRealtimeDb, deleteDataOnRealtimeDb } from '../utils/firebase';
import { toast } from 'react-toastify';
import moment from 'moment';

export const useGoal = () => {
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        achieved: 0,
        inProgress: 0,
        upcoming: 0
    });
    const [history, setHistory] = useState([]);
    const { currentUser } = useAuth();

    useEffect(() => {
        fetchGoals();
        fetchHistory();
    }, [currentUser]);

    const fetchGoals = async () => {
        try {
            const dbGoals = await getDataOnRealtimeDb(`users/${currentUser.uid}/goals`);
            if (!dbGoals) {
                setGoals([]);
                setLoading(false);
                return;
            }

            const goalsArray = Object.keys(dbGoals).map(key => ({
                id: key,
                name: dbGoals[key].name,
                targetAmount: dbGoals[key].targetAmount,
                currentAmount: dbGoals[key].currentAmount || 0,
                startDate: dbGoals[key].startDate,
                targetDate: dbGoals[key].targetDate,
                description: dbGoals[key].description,
                status: dbGoals[key].status || 'upcoming',
                priority: dbGoals[key].priority || 'medium',
                category: dbGoals[key].category || 'savings',
                notifyEmail: dbGoals[key].notifyEmail || false,
                notifyThreshold: dbGoals[key].notifyThreshold || 80 // Notify at 80% completion
            }));

            setGoals(goalsArray);
            calculateStats(goalsArray);
        } catch (error) {
            console.error("Error fetching goals:", error);
            toast.error("Failed to load goals");
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (goalList) => {
        const today = moment();
        
        const stats = goalList.reduce((acc, goal) => {
            const amount = parseFloat(goal.targetAmount || 0);
            acc.total += amount;

            const progress = (goal.currentAmount / goal.targetAmount) * 100;
            const targetDate = moment(goal.targetDate);
            const startDate = moment(goal.startDate);
            
            // Update status logic
            if (progress >= 100) {
                acc.achieved += amount;
            } else if (today.isAfter(targetDate)) {
                // If past target date and not achieved, count as in-progress
                acc.inProgress += amount;
                // Update goal status to in-progress if it's still marked as upcoming
                if (goal.status === 'upcoming') {
                    updateGoalStatus(goal.id, 'in-progress');
                }
            } else if (today.isBefore(startDate)) {
                acc.upcoming += amount;
            } else {
                acc.inProgress += amount;
            }

            return acc;
        }, { total: 0, achieved: 0, inProgress: 0, upcoming: 0 });

        setStats(stats);
    };

    const updateGoalStatus = async (id, status) => {
        try {
            const goal = goals.find(g => g.id === id);
            if (!goal) return;

            await updateDataOnRealtimeDb(
                `users/${currentUser.uid}/goals`,
                id,
                { ...goal, status }
            );
        } catch (error) {
            console.error('Error updating goal status:', error);
        }
    };

    const fetchHistory = async () => {
        try {
            const historyData = await getDataOnRealtimeDb(`users/${currentUser.uid}/goalHistory`);
            if (!historyData) {
                setHistory([]);
                return;
            }

            const historyArray = Object.entries(historyData)
                .map(([key, entry]) => {
                    // Normalize the history entry structure
                    return {
                        id: key,
                        timestamp: entry.timestamp,
                        goalName: entry.goalName || 'Unknown Goal',
                        goalCategory: entry.goalCategory || '-',
                        action: entry.action,
                        amountChange: entry.amountChange || 0,
                        currentAmount: entry.currentAmount || 0
                    };
                })
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

            setHistory(historyArray);
        } catch (error) {
            console.error('Error fetching history:', error);
        }
    };

    const logHistory = async (goalId, action, previousAmount = null, newAmount = null, notes = '') => {
        try {
            const goal = goals.find(g => g.id === goalId);
            if (!goal && action !== 'deleted') return;

            let amountChange = null;

            switch (action) {
                case 'created':
                    amountChange = 0;
                    break;
                case 'progress_updated':
                    amountChange = newAmount - (previousAmount || 0);
                    break;
                case 'updated':
                    amountChange = newAmount - previousAmount;
                    break;
                case 'deleted':
                    amountChange = -(goal?.currentAmount || 0);
                    break;
                case 'achieved':
                    amountChange = 0;
                    break;
            }

            const historyEntry = {
                id: goalId,
                timestamp: new Date().toISOString(),
                goalId,
                goalName: goal?.name || 'Deleted Goal',
                goalCategory: goal?.category || '-',
                action,
                amountChange,
                currentAmount: newAmount || 0
            };

            await saveDataOnRealtimeDb(`users/${currentUser.uid}/goalHistory`,historyEntry);

            await fetchHistory();
        } catch (error) {
            console.error('Error logging history:', error);
        }
    };

    const addGoal = async (values, { resetForm }) => {
        try {
            const { id, ...goalToAdd } = values;
            goalToAdd.currentAmount = 0;
            
            const today = moment();
            const startDate = moment(values.startDate);
            const targetDate = moment(values.targetDate);

            // Set initial status based on dates
            if (today.isBefore(startDate)) {
                goalToAdd.status = 'upcoming';
            } else if (today.isAfter(targetDate)) {
                goalToAdd.status = 'in-progress';
            } else {
                goalToAdd.status = 'in-progress';
            }

            await saveDataOnRealtimeDb(
                `users/${currentUser.uid}/goals`, 
                goalToAdd
            );

            await fetchGoals();
            await logHistory(id, 'created', null, goalToAdd.targetAmount, 'Goal created');
            resetForm();
            return { success: true, message: "Goal added successfully!" };
        } catch (error) {
            console.error('Error adding goal:', error);
            return { success: false, message: "Failed to add goal" };
        }
    };

    const updateGoal = async (values, { resetForm }) => {
        try {
            const { id, ...goalToUpdate } = values;
            
            // Calculate progress and update status
            const progress = (goalToUpdate.currentAmount / goalToUpdate.targetAmount) * 100;
            if (progress >= 100) {
                goalToUpdate.status = 'achieved';
            } else if (moment().isBefore(goalToUpdate.startDate)) {
                goalToUpdate.status = 'upcoming';
            } else {
                goalToUpdate.status = 'in-progress';
            }

            await updateDataOnRealtimeDb(
                `users/${currentUser.uid}/goals`, 
                id, 
                goalToUpdate
            );

            await fetchGoals();
            await logHistory(
                id, 
                'updated', 
                goals.find(g => g.id === id).currentAmount, 
                goalToUpdate.currentAmount,
                'Goal details updated'
            );
            resetForm();
            return { success: true, message: "Goal updated successfully!" };
        } catch (error) {
            console.error('Error updating goal:', error);
            return { success: false, message: "Failed to update goal" };
        }
    };

    const deleteGoal = async (id) => {
        try {
            await deleteDataOnRealtimeDb(`users/${currentUser.uid}/goals`, id);
            await fetchGoals();
            await logHistory(id, 'deleted', null, null, `Goal deleted`);
            return { success: true, message: "Goal deleted successfully!" };
        } catch (error) {
            console.error('Error deleting goal:', error);
            return { success: false, message: "Failed to delete goal" };
        }
    };

    const updateProgress = async (id, amount) => {
        try {
            const goal = goals.find(g => g.id === id);
            if (!goal) return { success: false, message: "Goal not found" };

            const oldAmount = goal.currentAmount || 0;
            const newAmount = parseFloat(oldAmount) + parseFloat(amount);
            const progress = (newAmount / goal.targetAmount) * 100;

            const updateData = {
                ...goal,
                currentAmount: newAmount,
                status: progress >= 100 ? 'achieved' : 'in-progress'
            };

            await updateDataOnRealtimeDb(
                `users/${currentUser.uid}/goals`, 
                id, 
                updateData
            );

            await logHistory(
                id,
                'progress_updated',
                oldAmount,
                newAmount,
                `Progress updated by ${amount}`
            );

            // If goal is achieved, log an additional achievement entry
            if (progress >= 100 && goal.status !== 'achieved') {
                await logHistory(id, 'achieved', null, null, 'Goal achieved!');
            }

            await fetchGoals();
            return { success: true, message: `Progress updated successfully!` };
        } catch (error) {
            console.error('Error updating goal progress:', error);
            return { success: false, message: "Failed to update progress" };
        }
    };

    return {
        goals,
        loading,
        stats,
        history,
        addGoal,
        updateGoal,
        deleteGoal,
        updateProgress
    };
}; 